<template>
  <div class="h5-query-container">
    <!-- 页面标题 -->
    <div class="h5-header">
      <a href="javascript:history.back()" class="back-button">
        <i class="el-icon-arrow-left"></i>
      </a>
      <h1>计量证书查询</h1>
    </div>

    <!-- 查询区域 -->
    <div class="h5-query-content">
      <div class="h5-query-form">
        <div class="h5-query-label">请输入计量证书编号</div>
        <el-input
          v-model="certificateNumber"
          placeholder="请输入计量证书编号"
          class="h5-certificate-input"
          @keyup.enter="handleQuery"
        />
        <el-button
          type="primary"
          class="h5-query-button"
          @click="handleQuery"
        >
          查询
        </el-button>
      </div>

      <!-- 查询结果区域 -->
      <div v-if="showResult" class="h5-result-container">
        <div class="h5-result-item">
          <span class="h5-result-label">证书编号</span>
          <span class="h5-result-value">{{ resultData.certificateCode }}</span>
        </div>
        <!-- 动态字段展示 -->
        <div v-if="dynamicFields && dynamicFields.length > 0">
          <div class="h5-result-item" v-for="(item, index) in dynamicFields" :key="index">
            <span class="h5-result-label">{{ item.header }}</span>
            <span class="h5-result-value">{{ item.value || '/' }}</span>
          </div>
        </div>
      </div>

      <!-- 无结果提示 -->
      <div v-else-if="showNoResult" class="h5-no-result">
        <p>未找到该证书信息，请检查证书编号是否正确</p>
        <el-button type="primary" class="h5-retry-button" @click="resetQuery">重新查询</el-button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { useRoute } from 'vue-router'
import certificateAPI from '@/api/certificate.js'

const certificateNumber = ref('')
const showResult = ref(false)
const showNoResult = ref(false)
const resultData = reactive({})
// 存储动态字段数据
const dynamicFields = ref([])

// 获取路由实例
const route = useRoute()

// 处理查询
const handleQuery = async () => {
  if (!certificateNumber.value.trim()) {
    ElMessage.warning('请输入证书编号')
    return
  }

  // 显示加载状态
  ElMessage({ message: '查询中...', type: 'info', duration: 0 })

  try {
    // 调用API查询证书
    // 响应拦截器已处理code=0的情况并直接返回data
    const data = await certificateAPI.queryCertificate(certificateNumber.value)
    
    // 映射API返回的数据到结果对象（根据新接口返回的CertificateInfo对象结构）
    Object.assign(resultData, {
      certificateCode: data.certificateCode || '',
      certificateCompanyName: data.certificateCompanyName || '',
      instrumentName: data.instrumentName || '',
      modelSpecification: data.modelSpecification || '',
      factorySerialCode: data.factorySerialCode || '',
      manufacturer: data.manufacturer || '',
      quantity: data.quantity || '',
      updateTime: data.updateTime || ''
    })
    
    // 处理动态字段
    dynamicFields.value = []
    if (data.jsonData) {
      try {
        // 解析jsonData为数组
        const jsonDataArray = JSON.parse(data.jsonData)
        if (Array.isArray(jsonDataArray)) {
          dynamicFields.value = jsonDataArray.filter(item => item.header && item.value !== undefined)
        }
      } catch (parseError) {
        console.error('解析jsonData失败:', parseError)
      }
    }
    
    showResult.value = true
    showNoResult.value = false
  } catch (error) {
    // 在生产环境中可能是真实的API错误，但在开发环境可能只是无法连接服务器
    ElMessage.error('查询失败，请稍后重试')
    console.error('查询证书失败:', error)
    showResult.value = false
    showNoResult.value = true
  } finally {
    // 关闭加载状态
    ElMessage.closeAll()
  }
}

// 重置查询
const resetQuery = () => {
  certificateNumber.value = ''
  showResult.value = false
  showNoResult.value = false 
  // 重新聚焦到输入框
  setTimeout(() => {
    const inputElement = document.querySelector('.h5-certificate-input .el-input__inner')
    if (inputElement) {
      inputElement.focus()
    }
  }, 100)
}

// 组件挂载时检查URL参数
onMounted(() => {
  // 从URL查询参数中获取证书编号，支持多种常见的参数名格式
  const certNoFromUrl = route.query.certificateNo || 
                      route.query.certificateNumber || 
                      route.query.certNo || 
                      route.query.code || 
                      route.query.id || ''
  
  if (certNoFromUrl) {
    // 设置证书编号
    certificateNumber.value = certNoFromUrl
    
    // 自动触发查询，并提供扫码场景下的明确反馈
    // 延迟执行查询，确保DOM已完全渲染
    setTimeout(() => {
      handleQuery()
    }, 200)
  }
})
</script>

<style scoped>
/* 导入全局样式 */
@import '@/style/global.css';

/* H5证书查询页面特有样式 */

.h5-query-container {
  min-height: 100vh;
  background-color: #f2f2f2;
  display: flex;
  flex-direction: column;
}

.h5-header {
  background-color: #1e88e5;
  color: white;
  text-align: center;
  padding: 15px 0;
  position: relative;
}

.h5-header h1 {
  font-size: 18px;
  font-weight: 500;
  margin: 0;
}

.back-button {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: white;
  font-size: 18px;
}

.h5-query-content {
  flex: 1;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.h5-query-form {
  width: 100%;
  max-width: 500px;
}

.h5-query-label {
  font-size: 15px;
  color: #333;
  margin-bottom: 12px;
  text-align: center;
}

.h5-certificate-input {
  width: 100%;
  margin-bottom: 16px;
}

.h5-certificate-input .el-input__inner {
  height: 40px;
  font-size: 15px;
  border-radius: 4px;
  border: 1px solid #ddd;
}

.h5-query-button {
  width: 100%;
  height: 40px;
  font-size: 15px;
  border-radius: 4px;
  background-color: #e0e0e0;
  color: #333;
  border: none;
}

.h5-query-button:hover {
  background-color: #d0d0d0;
}

.h5-result-container {
  width: 100%;
  max-width: 500px;
  margin-top: 16px;
}

.h5-result-item {
  display: flex;
  border-bottom: 1px solid #e0e0e0;
  background-color: white;
}

.h5-result-label {
    font-size: 16px;
    color: black;
    width: 100px;
    flex-shrink: 0;
    background-color: rgb(170, 210, 240);
    padding: 12px 16px;
    display: flex;
    align-items: center;
  }

.h5-result-value {
  font-size: 17px;
  color: #333;
  word-break: break-word;
  flex: 1;
  padding: 12px 16px;
  background-color: white;
}

.h5-no-result {
  text-align: center;
  padding: 40px 20px;
  color: #666;
  background-color: white;
  margin-top: 16px;
}

.h5-no-result p {
  margin-bottom: 16px;
  font-size: 15px;
}

.h5-retry-button {
  height: 40px;
  font-size: 15px;
  padding: 0 24px;
  border-radius: 4px;
  background-color: #1e88e5;
  border: none;
  color: white;
}

/* 适配小屏幕设备 */
@media screen and (max-width: 360px) {
  .h5-header {
    padding: 12px 0;
  }
  
  .h5-header h1 {
    font-size: 16px;
  }
  
  .h5-query-content {
    padding: 12px;
  }
  
  .h5-query-label {
    font-size: 14px;
  }
  
  .h5-certificate-input .el-input__inner,
  .h5-query-button,
  .h5-retry-button {
    height: 36px;
    font-size: 14px;
  }
  
  .h5-result-item {
    padding: 12px;
  }
  
  .h5-result-label {
    font-size: 15px;
    width: 80px;
  }
  
  .h5-result-value {
    font-size: 16px;
  }
  
  .h5-no-result {
    padding: 12px;
  }
}

/* 滚动条样式 */
::-webkit-scrollbar {
  width: 4px;
  height: 4px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 2px;
}

::-webkit-scrollbar-thumb:hover {
  background: #999;
}
</style>